import type { IMetricControl } from '~/modules/page-analytic/types';
import type { EMetricKey } from '~/types/metric';

import { usePageAnalyticDetailContext } from '~/modules/page-analytic/contexts/PageAnalyticDetailProvider';
import { BlockStack, Card, Icon, InlineStack, Text } from '@shopify/polaris';
import { usePageAnalyticFilterStore } from '~/modules/page-analytic/stores';
import { LOCATION_PATH_KEY } from '~/modules/page-analytic/constants';
import { PAMetricInfoSkeleton } from './common/PAMetricInfoSkeleton';
import { EReportSource } from '~/modules/report-analytic/types';
import { useNavigateToReport } from '~/modules/report-analytic';
import { ANALYTICS_METRIC_TOOLTIP } from '~/modules/analytics';
import { ChevronRightIcon } from '@shopify/polaris-icons';
import { GDiv, GTooltipCard } from '~/presentation/ui';
import { useSearchParams } from '@remix-run/react';
import { useToggle } from '~/hooks/useToggle';
import { PALineChart } from './PALineChart';
import { PAPercent } from './PAPercent';

export interface PACardLineChartProps {
  title: string;
  metricKey: EMetricKey;
  control: IMetricControl;
  isLoading?: boolean;
}
export const PACardLineChart: React.FC<PACardLineChartProps> = ({
  title,
  metricKey,
  control,
  isLoading,
}) => {
  const analyticMode = usePageAnalyticFilterStore((state) => state.analyticMode);

  const { value, change } = control;
  const { hideComparison } = usePageAnalyticDetailContext();
  const { handleNavigateToReport } = useNavigateToReport();
  const { setFalse: setFalseHover, setTrue: setTrueHover, value: isHover } = useToggle(false);

  const [searchParams] = useSearchParams();

  const tooltip = ANALYTICS_METRIC_TOOLTIP[metricKey];
  const locationPath = searchParams.get(LOCATION_PATH_KEY);

  return (
    <div onMouseEnter={setTrueHover} onMouseLeave={setFalseHover}>
      <Card>
        <BlockStack gap="200">
          {isLoading ? (
            <PAMetricInfoSkeleton />
          ) : (
            <BlockStack gap="200">
              <InlineStack>
                <GDiv
                  className="hover:cursor-pointer hover:text-[--p-color-text-link-hover]"
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleNavigateToReport(metricKey, {
                      locationPath,
                      mode: analyticMode,
                      from: EReportSource.PAGE,
                    });
                  }}
                >
                  <InlineStack>
                    <GTooltipCard tooltip={tooltip}>
                      <Text as="h3" variant="headingMd">
                        {title}
                      </Text>
                    </GTooltipCard>
                    {isHover && <Icon source={ChevronRightIcon} tone="inherit" />}
                  </InlineStack>
                </GDiv>
              </InlineStack>
              <PAPercent
                value={value}
                change={change}
                hideComparison={hideComparison}
                metricKey={metricKey}
              />
            </BlockStack>
          )}
          <PALineChart activeTab={metricKey} />
        </BlockStack>
      </Card>
    </div>
  );
};
